import React, { useState, useEffect } from 'react';
import { X, User, Phone, Tag, DollarSign, Briefcase, Loader2, Sparkles, AlignLeft } from 'lucide-react';
import { createLead, getUsers } from '../../../services/api';

const CreateLeadModal = ({ isOpen, onClose, onRefresh }) => {
  const [loading, setLoading] = useState(false);
  const [managers, setManagers] = useState([]);
  const [formData, setFormData] = useState({
    client_name: '',
    phone: '',
    title: '',
    price: '',
    description: '',
    user_id: ''
  });

  const handlePhoneChange = (e) => {
    let input = e.target.value;
    if (!input) {
      setFormData({ ...formData, phone: '' });
      return;
    }
    let digits = input.replace(/\D/g, '');
    if (digits.startsWith('7') || digits.startsWith('8')) {
      digits = digits.substring(1);
    }
    digits = digits.substring(0, 10);
    let formattedValue = '+7';
    if (digits.length > 0) formattedValue += ' (' + digits.substring(0, 3);
    if (digits.length >= 4) formattedValue += ') ' + digits.substring(3, 6);
    if (digits.length >= 7) formattedValue += '-' + digits.substring(6, 8);
    if (digits.length >= 9) formattedValue += '-' + digits.substring(8, 10);
    setFormData({ ...formData, phone: formattedValue });
  };

  useEffect(() => {
    if (isOpen) {
      getUsers().then(setManagers).catch(console.error);
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.phone.length < 18) {
      alert('Введите полный номер телефона');
      return;
    }
    if (!formData.user_id) {
      alert('Пожалуйста, выберите ответственного менеджера');
      return;
    }
    setLoading(true);
    try {
      await createLead({
        ...formData,
        price: formData.price || 0,
        user_id: formData.user_id 
      });
      onRefresh();
      onClose();
      setFormData({ client_name: '', phone: '', title: '', price: '', description: '', user_id: '' });
    } catch (err) {
      alert('Ошибка при создании лида');
    } finally {
      setLoading(false);
    }
  };

  const formatDisplayPrice = (value) => {
    if (!value) return '';
    const digits = value.toString().replace(/\D/g, '');
    return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  };

  const cleanPrice = (value) => value.replace(/\s/g, '');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[150] flex justify-end bg-slate-950/60 backdrop-blur-md transition-all duration-500">
      <div className="absolute inset-0" onClick={onClose} />
      
      <div className="relative w-full max-w-lg bg-[#1e293b] h-full shadow-[-20px_0_50px_rgba(0,0,0,0.5)] flex flex-col border-l border-white/5 animate-in slide-in-from-right duration-300">
        
        {/* Header - Адаптивные отступы и размер шрифта */}
        <div className="p-5 sm:p-8 border-b border-white/5 bg-[#1e293b] flex-shrink-0">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-indigo-400 mb-1">
                <Sparkles size={12} />
                <span className="text-[9px] sm:text-[10px] font-black uppercase tracking-[0.3em]">Новая возможность</span>
              </div>
              <h2 className="text-xl sm:text-3xl font-black text-white tracking-tighter uppercase italic truncate">
                Создать сделку
              </h2>
            </div>
            <button 
              onClick={onClose} 
              className="flex-shrink-0 p-2 sm:p-3 min-h-[40px] min-w-[40px] flex items-center justify-center bg-white/5 hover:bg-rose-500/20 text-slate-400 hover:text-rose-500 rounded-xl sm:rounded-2xl transition-all border border-white/5"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Form - Уменьшены отступы и интервалы для мобилок */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 sm:p-8 space-y-5 sm:space-y-8 min-h-0 bg-[#1e293b] custom-scrollbar">
          
          <InputGroup label="Суть сделки" icon={<Briefcase size={13}/>}>
            <input
              required
              autoFocus
              type="text"
              placeholder="Напр: Оптовая поставка оборудования"
              className="w-full bg-slate-900/50 border border-white/5 rounded-xl sm:rounded-2xl p-3 sm:p-4 text-xs sm:text-sm text-white focus:ring-2 focus:ring-indigo-500 transition-all outline-none placeholder:text-slate-600 font-bold"
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
            />
          </InputGroup>

          <InputGroup label="Описание сделки" icon={<AlignLeft size={13}/>}>
            <textarea
              placeholder="Дополнительная информация..."
              rows={2}
              className="w-full bg-slate-900/50 border border-white/5 rounded-xl sm:rounded-2xl p-3 sm:p-4 text-xs sm:text-sm text-white focus:ring-2 focus:ring-indigo-500 transition-all outline-none placeholder:text-slate-600 font-medium resize-none"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
            />
          </InputGroup>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <InputGroup label="Клиент" icon={<User size={13}/>}>
              <input
                required
                type="text"
                placeholder="Имя Фамилия"
                className="w-full bg-slate-900/50 border border-white/5 rounded-xl sm:rounded-2xl p-3 sm:p-4 text-xs sm:text-sm text-white focus:ring-2 focus:ring-indigo-500 outline-none placeholder:text-slate-600 font-bold"
                value={formData.client_name}
                onChange={(e) => setFormData({...formData, client_name: e.target.value})}
              />
            </InputGroup>

            <InputGroup label="Телефон" icon={<Phone size={13}/>}>
              <input
                required
                type="text"
                placeholder="+7 (707) 000-00-00"
                className="w-full bg-slate-900/50 border border-white/5 rounded-xl sm:rounded-2xl p-3 sm:p-4 text-xs sm:text-sm text-white focus:ring-2 focus:ring-indigo-500 outline-none placeholder:text-slate-600 font-bold transition-all"
                value={formData.phone}
                onChange={handlePhoneChange}
              />
            </InputGroup>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <InputGroup label="Бюджет (₸)" icon={<DollarSign size={13}/>}>
              <div className="relative">
                <input
                  type="text"
                  placeholder="0"
                  className="w-full bg-slate-900/50 border border-white/5 rounded-xl sm:rounded-2xl p-3 sm:p-4 pl-9 sm:pl-10 text-base sm:text-lg font-black text-indigo-400 focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                  value={formatDisplayPrice(formData.price)}
                  onChange={(e) => {
                    const rawValue = cleanPrice(e.target.value);
                    if (/^\d*$/.test(rawValue)) {
                      setFormData({...formData, price: rawValue});
                    }
                  }}
                />
                <div className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-indigo-500/50 font-black text-sm sm:text-base">₸</div>
              </div>
            </InputGroup>

            <InputGroup label="Ответственный *" icon={<Tag size={13}/>}>
              <div className="relative">
                <select
                  required 
                  className={`w-full bg-slate-900/50 border rounded-xl sm:rounded-2xl p-3 sm:p-4 text-xs sm:text-sm text-white focus:ring-2 focus:ring-indigo-500 outline-none font-bold appearance-none cursor-pointer transition-all ${
                    !formData.user_id ? 'border-rose-500/50 text-slate-400' : 'border-white/5'
                  }`}
                  value={formData.user_id}
                  onChange={(e) => setFormData({...formData, user_id: e.target.value})}
                >
                  <option value="" className="bg-slate-900">Выбрать...</option>
                  {managers.map(m => (
                    <option key={m.id} value={m.id} className="bg-slate-900">{m.name}</option>
                  ))}
                </select>
                <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500">
                  <Tag size={12} />
                </div>
              </div>
            </InputGroup>
          </div>
        </form>

        {/* Footer - Сделали чуть компактнее по высоте */}
        <div className="p-5 sm:p-8 border-t border-white/5 bg-[#1e293b] flex gap-3 sm:gap-4">
          <button type="button" onClick={onClose} className="flex-1 px-4 sm:px-6 py-3 sm:py-4 rounded-xl sm:rounded-2xl font-black text-[9px] sm:text-[10px] uppercase tracking-widest text-slate-400 hover:bg-white/5 transition-all border border-white/5">
            Отмена
          </button>
          <button onClick={handleSubmit} disabled={loading} className="flex-[2] bg-indigo-600 hover:bg-indigo-500 text-white px-4 sm:px-6 py-3 sm:py-4 rounded-xl sm:rounded-2xl font-black text-[9px] sm:text-[10px] uppercase tracking-widest flex items-center justify-center gap-2 sm:gap-3 transition-all shadow-lg shadow-indigo-500/20 disabled:opacity-50 group">
            {loading ? <Loader2 className="animate-spin" size={14} /> : <>Создать <span className="hidden xs:inline">сделку</span> <Sparkles size={14} /></>}
          </button>
        </div>
      </div>
      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(99, 102, 241, 0.2); border-radius: 10px; }
      `}</style>
    </div>
  );
};

const InputGroup = ({ label, icon, children }) => (
  <div className="space-y-2 sm:space-y-3">
    <label className="text-[9px] sm:text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] flex items-center gap-2 ml-1">
      <span className="text-indigo-500">{icon}</span> {label}
    </label>
    {children}
  </div>
);

export default CreateLeadModal;